import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useContext,
} from 'react';
import pt from 'prop-types';
import cx from 'classnames';

import { RootContext } from 'components/storefront/editor/easyblocks-page/components/root';
import useOverlayPosition from 'components/storefront/editor/easyblocks-page/components/overlay/use-overlay-position';

import SectionLabel from './label';
import AddSectionButtons from './add-section-buttons';
import {
  parsePath,
  isFirstHeaderSection,
  isLastFooterSection,
} from './add-section-buttons/utils';
import './section-overlay.scss';

const SCROLL_OFFSET = 50;

function SectionOverlay({
  path,
  selected,
  blockSelected,
  hovered,
  blockHovered,
  sidebarHovered,
  sectionSchema,
  sectionGroups,
  contentRef,
}) {
  const [showSectionModal, setShowSectionModal] = useState(false);
  const overlayRef = useRef(null);

  const rootContext = useContext(RootContext);

  const isPositioned = useOverlayPosition(contentRef, overlayRef);

  const [group, index] = useMemo(() => parsePath(path), [path]);
  const groupSections = rootContext[group];
  const sectionsLength = groupSections?.length;

  useEffect(() => {
    if (!overlayRef.current || !isPositioned || (!selected && !blockSelected)) {
      return;
    }

    const rect = overlayRef.current.getBoundingClientRect();

    window.scrollTo({
      top: rect.top + window.scrollY - SCROLL_OFFSET,
      behavior: 'smooth',
    });
  }, [isPositioned, selected, blockSelected]);

  const onOpenSectionModal = useCallback(() => {
    setShowSectionModal(true);
  }, []);

  const onCloseSectionModal = useCallback(() => {
    setShowSectionModal(false);
  }, []);

  return (
    <div
      ref={overlayRef}
      className={cx('section-overlay', {
        selected,
        'block-selected': blockSelected,
        hovered: hovered,
        'block-hovered': blockHovered,
        'sidebar-hovered': sidebarHovered,
        'with-buttons': showSectionModal,
        'first-section': isFirstHeaderSection(group, sectionGroups, index),
        'last-section': isLastFooterSection(
          group,
          sectionGroups,
          index,
          sectionsLength,
        ),
      })}
    >
      <SectionLabel label={sectionSchema?.label} />
      {!selected && !blockSelected && (
        <AddSectionButtons
          path={path}
          sectionGroups={sectionGroups}
          onOpenSectionModal={onOpenSectionModal}
          onCloseSectionModal={onCloseSectionModal}
        />
      )}
    </div>
  );
}

SectionOverlay.propTypes = {
  path: pt.string,
  selected: pt.bool,
  blockSelected: pt.bool,
  hovered: pt.bool,
  blockHovered: pt.bool,
  sidebarHovered: pt.bool,
  sectionSchema: pt.object,
  sectionGroups: pt.array,
  contentRef: pt.object,
};

export default React.memo(SectionOverlay);
